import {
    Box,
    Button,
    Flex,
    Grid,
    HStack,
    SimpleGrid,
    Skeleton,
    Stack,
    Tag,
    Text,
    useColorMode,
    useColorModeValue,
} from "@chakra-ui/react"
import Apex from "react-apexcharts"
import { Container, Icon, Scope } from "components"
import { Enums, Mask, Moment } from "config"
import { useOrdersList, useOrdersMonthlyData, useReport, useScreens, useUser, useWithdraws } from "hooks"
import useActiveConsultantsCount from "../../hooks/useActiveConsultantsCount"
import { useState } from "react"
import { useNavigate } from "react-router-dom"

// ─── KPI Card ──────────────────────────────────────────────────────────────────

function KpiCard({ icon, label, sublabel, value, accent, loading }) {
    const bg        = useColorModeValue("white", "gray.700")
    const border    = useColorModeValue("gray.100", "gray.600")
    const labelColor   = useColorModeValue("gray.500", "gray.400")
    const sublabelColor = useColorModeValue("gray.400", "gray.500")

    return (
        <Box
            bg={bg}
            borderWidth={1}
            borderColor={border}
            borderRadius="xl"
            p={5}
            borderTopWidth={3}
            borderTopColor={accent}
        >
            <HStack spacing={3} mb={4} align="center">
                <Box
                    bg={`${accent}22`}
                    p="9px"
                    borderRadius="lg"
                    flexShrink={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <Icon name={icon} color={accent} boxSize={5} />
                </Box>
                <Box minW={0}>
                    <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        textTransform="uppercase"
                        letterSpacing="wider"
                        color={labelColor}
                        noOfLines={1}
                    >
                        {label}
                    </Text>
                    {sublabel && (
                        <Text fontSize="xs" color={sublabelColor} noOfLines={1}>
                            {sublabel}
                        </Text>
                    )}
                </Box>
            </HStack>

            {loading ? (
                <Skeleton height="2rem" width="55%" borderRadius="md" />
            ) : (
                <Text fontSize="2xl" fontWeight="bold" lineHeight={1}>
                    {value ?? "—"}
                </Text>
            )}
        </Box>
    )
}

// ─── Pending Row ───────────────────────────────────────────────────────────────

function PendingRow({ icon, accent, label, count, onClick, loading }) {
    const hover      = useColorModeValue("gray.50", "gray.600")
    const arrowColor = useColorModeValue("gray.300", "gray.500")

    if (loading) return <Skeleton height="44px" borderRadius="lg" />
    if (!count) return null

    return (
        <HStack
            justify="space-between"
            p={3}
            borderRadius="lg"
            cursor="pointer"
            _hover={{ bg: hover }}
            onClick={onClick}
            transition="background 0.15s"
            role="group"
        >
            <HStack spacing={3}>
                <Box w="7px" h="7px" borderRadius="full" bg={accent} flexShrink={0} />
                <Icon name={icon} color={accent} boxSize={4} />
                <Text fontSize="sm">{label}</Text>
            </HStack>
            <HStack spacing={2} flexShrink={0}>
                <Tag
                    size="sm"
                    bg={`${accent}22`}
                    color={accent}
                    fontWeight="bold"
                    borderRadius="full"
                    px={2}
                >
                    {count}
                </Tag>
                <Icon
                    name="right"
                    boxSize={3}
                    color={arrowColor}
                    _groupHover={{ color: accent }}
                    transition="color 0.15s"
                />
            </HStack>
        </HStack>
    )
}

// ─── Recent Order Row ──────────────────────────────────────────────────────────

function RecentOrderRow({ item, onOpen }) {
    const hover = useColorModeValue("gray.50", "gray.600")
    const muted = useColorModeValue("gray.400", "gray.500")
    const statusData = Enums.OrderStatus.data(item.status)
    const clientName = item.distributor_to?.name ?? item.consultant?.customer?.name ?? "—"

    return (
        <HStack
            justify="space-between"
            px={3}
            py="10px"
            borderRadius="lg"
            cursor="pointer"
            _hover={{ bg: hover }}
            onClick={() => onOpen(item)}
            transition="background 0.15s"
            gap={2}
            overflow="hidden"
        >
            <HStack spacing={2} minW={0} flex={1} overflow="hidden">
                <Text
                    fontSize="xs"
                    color={muted}
                    fontFamily="mono"
                    flexShrink={0}
                    minW="44px"
                    textAlign="right"
                >
                    #{item.cod}
                </Text>
                <Tag
                    size="sm"
                    colorScheme={statusData.colorScheme}
                    variant="subtle"
                    flexShrink={0}
                    borderRadius="full"
                    px={2}
                    display={{ base: "none", sm: "flex" }}
                >
                    {item.status_label}
                </Tag>
                <Text fontSize="sm" noOfLines={1} flex={1} minW={0}>
                    {clientName}
                </Text>
            </HStack>
            <HStack spacing={2} flexShrink={0}>
                <Text fontSize="sm" fontWeight="semibold" display={{ base: "none", sm: "block" }}>
                    R$ {Mask.price(item.total_amount)}
                </Text>
                <Text fontSize="xs" color={muted} flexShrink={0}>
                    {Moment(item.created_at).format("DD/MM")}
                </Text>
            </HStack>
        </HStack>
    )
}

// ─── Quick Button ──────────────────────────────────────────────────────────────

function QuickButton({ icon, label, accent, onClick }) {
    const bg = useColorModeValue("gray.50", "gray.600")
    const hover = useColorModeValue("gray.100", "gray.500")

    return (
        <Button
            variant="ghost"
            bg={bg}
            _hover={{ bg: hover, transform: "translateY(-1px)" }}
            borderRadius="xl"
            h={16}
            flexDirection="column"
            gap={1}
            onClick={onClick}
            transition="all 0.15s"
            fontSize="xs"
            fontWeight="medium"
        >
            <Icon name={icon} color={accent} boxSize={5} />
            <Text fontSize="xs">{label}</Text>
        </Button>
    )
}

// ─── Section Card ──────────────────────────────────────────────────────────────

function Card({ title, icon, accent, action, actionLabel, children }) {
    const bg = useColorModeValue("white", "gray.700")
    const border = useColorModeValue("gray.100", "gray.600")

    return (
        <Box bg={bg} borderWidth={1} borderColor={border} borderRadius="xl" p={{ base: 4, md: 6 }}>
            <Flex justify="space-between" align="center" mb={4}>
                <HStack spacing={2}>
                    <Icon name={icon} color={accent} boxSize={4} />
                    <Text fontWeight="semibold" fontSize="sm">
                        {title}
                    </Text>
                </HStack>
                {action && (
                    <Button size="xs" variant="ghost" color={accent} onClick={action} fontWeight="medium">
                        {actionLabel} →
                    </Button>
                )}
            </Flex>
            {children}
        </Box>
    )
}

// ─── Orders Chart ──────────────────────────────────────────────────────────────

function OrdersChart() {
    const { colorMode } = useColorMode()
    const bg     = useColorModeValue("white",    "gray.700")
    const border = useColorModeValue("gray.100", "gray.600")
    const muted  = useColorModeValue("gray.400", "gray.500")
    const accent = "#008AAC"

    const { isLoading, dates, values } = useOrdersMonthlyData()

    const labelColor = colorMode === "dark" ? "#718096" : "#A0AEC0"
    const series = [{ name: "Pedidos", data: values }]

    const options = {
        theme: { mode: colorMode },
        chart: {
            type: "bar",
            background: "transparent",
            toolbar: { show: false },
            locales: [require("apexcharts/dist/locales/pt.json")],
            defaultLocale: "pt",
            animations: { enabled: true, easing: "easeinout", speed: 500 },
        },
        plotOptions: {
            bar: { borderRadius: 5, columnWidth: "52%" },
        },
        colors: [accent],
        fill: {
            type: "gradient",
            gradient: {
                shade: "light",
                type: "vertical",
                gradientToColors: ["#00596F"],
                stops: [0, 100],
            },
        },
        dataLabels: { enabled: false },
        grid: { show: false },
        xaxis: {
            type: "datetime",
            categories: dates,
            labels: {
                format: "MMM/yy",
                style: { colors: Array(12).fill(labelColor) },
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: { show: false },
        tooltip: {
            theme: colorMode,
            x: { format: "MMMM [de] yyyy" },
            y: { formatter: (v) => `${v} pedido${v !== 1 ? "s" : ""}` },
        },
    }

    return (
        <Box bg={bg} borderWidth={1} borderColor={border} borderRadius="xl" p={{ base: 4, md: 6 }} mb={5} overflow="hidden">
            <Flex justify="space-between" align="center" mb={3}>
                <HStack spacing={2}>
                    <Icon name="reports" color={accent} boxSize={4} />
                    <Text fontWeight="semibold" fontSize="sm">Pedidos mês a mês</Text>
                </HStack>
                <Text fontSize="xs" color={muted}>Últimos 12 meses</Text>
            </Flex>

            {isLoading ? (
                <Skeleton height="200px" borderRadius="lg" />
            ) : (
                <Apex options={options} series={series} type="bar" height={200} width="100%" />
            )}
        </Box>
    )
}

// ─── Home ──────────────────────────────────────────────────────────────────────

export default function Home() {
    const navigate = useNavigate()
    const { setOrder } = useScreens()
    const user = useUser()

    const muted      = useColorModeValue("gray.500", "gray.400")
    const allGoodBg  = useColorModeValue("green.50", "whiteAlpha.50")

    // Greeting
    const hour = new Date().getHours()
    const greeting = hour < 12 ? "Bom dia" : hour < 18 ? "Boa tarde" : "Boa noite"
    const firstName = user.data?.name?.split(" ")[0] ?? ""

    // Stable date ranges (computed once at mount)
    const [todayPeriod] = useState([Moment().startOf("day").toDate(), Moment().toDate()])
    const [recentPeriod] = useState([Moment().subtract(30, "days").toDate(), Moment().toDate()])
    const [backlogPeriod] = useState([Moment().subtract(1, "year").toDate(), Moment().toDate()])

    // KPI data
    const clientsTotal        = useReport("clients_total", Enums.Period.ALL_PERIOD)
    const activeThisMonth     = useActiveConsultantsCount()
    const ordersToday         = useOrdersList(1, null, null, null, null, "all", "created_at", todayPeriod)
    const withdrawsAnalyzing  = useWithdraws(1, null, Enums.WithdrawStatus.ANALYZING)

    // Backlog by status
    const ordersAnalyzing      = useOrdersList(1, null, null, Enums.OrderStatus.ANALYZING,       null, "all", "created_at", backlogPeriod)
    const ordersQuotation      = useOrdersList(1, null, null, Enums.OrderStatus.QUOTATION,        null, "all", "created_at", backlogPeriod)
    const ordersMontage        = useOrdersList(1, null, null, Enums.OrderStatus.MONTAGE,          null, "all", "created_at", backlogPeriod)
    const ordersWaitingShip    = useOrdersList(1, null, null, Enums.OrderStatus.WAITING_SHIPPING, null, "all", "created_at", backlogPeriod)

    // Recent orders list
    const recentOrders = useOrdersList(1, null, null, null, null, "all", "created_at", recentPeriod)

    const pendingLoading =
        ordersAnalyzing.isLoading ||
        ordersQuotation.isLoading ||
        ordersMontage.isLoading ||
        ordersWaitingShip.isLoading ||
        withdrawsAnalyzing.isLoading

    const totalPendencies =
        (ordersAnalyzing.data?.meta?.total    ?? 0) +
        (ordersQuotation.data?.meta?.total    ?? 0) +
        (ordersMontage.data?.meta?.total      ?? 0) +
        (ordersWaitingShip.data?.meta?.total  ?? 0) +
        (withdrawsAnalyzing.data?.meta?.total ?? 0)

    return (
        <Container maxW={{ base: "100%", md: "90%" }} px={{ base: 3, md: 6 }} mt={8} mb={16}>

            {/* ── Greeting ──────────────────────────────────────────────── */}
            <Box mb={6}>
                <Text fontSize="2xl" fontWeight="bold" lineHeight={1.2}>
                    {greeting}{firstName ? `, ${firstName}` : ""}
                </Text>
                <Text fontSize="sm" color={muted} mt={1} textTransform="capitalize">
                    {Moment().format("dddd")}, {Moment().format("LL")}
                </Text>
            </Box>

            {/* ── KPI Cards ─────────────────────────────────────────────── */}
            <SimpleGrid columns={{ base: 2, lg: 4 }} gap={4} mb={6}>

                <Scope role="orders_view">
                    <KpiCard
                        icon="orders"
                        label="Pedidos hoje"
                        sublabel="Registrados no dia"
                        value={ordersToday.data?.meta?.total ?? "—"}
                        accent="#008AAC"
                        loading={ordersToday.isLoading}
                    />
                </Scope>

                <Scope role="withdraws_manage">
                    <KpiCard
                        icon="financial"
                        label="Saques pendentes"
                        sublabel="Aguardando aprovação"
                        value={withdrawsAnalyzing.data?.meta?.total ?? "—"}
                        accent="#E53E3E"
                        loading={withdrawsAnalyzing.isLoading}
                    />
                </Scope>

                <Scope role="reports_consultants">
                    <KpiCard
                        icon="consultants"
                        label="Consultores"
                        sublabel="Total cadastrados"
                        value={clientsTotal.data?.data ?? "—"}
                        accent="#38A169"
                        loading={clientsTotal.isLoading}
                    />
                </Scope>

                <Scope role="reports_consultants">
                    <KpiCard
                        icon="consultants"
                        label="Ativos no mês"
                        sublabel={Moment().format("MMMM [de] YYYY")}
                        value={activeThisMonth.data?.data ?? "—"}
                        accent="#805AD5"
                        loading={activeThisMonth.isLoading}
                    />
                </Scope>

            </SimpleGrid>

            {/* ── Orders Chart ──────────────────────────────────────────── */}
            <Scope role="orders_view">
                <OrdersChart />
            </Scope>

            {/* ── Main Grid ─────────────────────────────────────────────── */}
            <Grid templateColumns={{ base: "1fr", xl: "1fr 300px" }} gap={5}>

                {/* ── Left column ─────────────────────────────────────── */}
                <Stack spacing={5}>

                    {/* Pendências críticas */}
                    <Scope role="orders_view">
                        <Card
                            title="Pendências críticas"
                            icon="error"
                            accent="#DD6B20"
                            action={() => navigate("/orders")}
                            actionLabel="Ver pedidos"
                        >
                            {pendingLoading ? (
                                <Stack spacing={2}>
                                    <Skeleton height="44px" borderRadius="lg" />
                                    <Skeleton height="44px" borderRadius="lg" />
                                    <Skeleton height="44px" borderRadius="lg" />
                                </Stack>
                            ) : totalPendencies === 0 ? (
                                <HStack
                                    spacing={3}
                                    p={4}
                                    borderRadius="lg"
                                    bg={allGoodBg}
                                >
                                    <Icon name="check" color="green.400" boxSize={5} />
                                    <Text fontSize="sm" color="green.500" fontWeight="medium">
                                        Tudo em dia — nenhuma pendência encontrada.
                                    </Text>
                                </HStack>
                            ) : (
                                <Stack spacing={1}>
                                    <Scope role="withdraws_manage">
                                        <PendingRow
                                            icon="financial"
                                            accent="#E53E3E"
                                            label={`${withdrawsAnalyzing.data?.meta?.total} saque(s) aguardando aprovação`}
                                            count={withdrawsAnalyzing.data?.meta?.total}
                                            onClick={() => navigate("/withdraws")}
                                            loading={withdrawsAnalyzing.isLoading}
                                        />
                                    </Scope>
                                    <PendingRow
                                        icon="info"
                                        accent="#DD6B20"
                                        label={`${ordersAnalyzing.data?.meta?.total} pedido(s) em análise`}
                                        count={ordersAnalyzing.data?.meta?.total}
                                        onClick={() => navigate("/orders")}
                                        loading={ordersAnalyzing.isLoading}
                                    />
                                    <PendingRow
                                        icon="quotation"
                                        accent="#D69E2E"
                                        label={`${ordersQuotation.data?.meta?.total} pedido(s) em cotação`}
                                        count={ordersQuotation.data?.meta?.total}
                                        onClick={() => navigate("/orders")}
                                        loading={ordersQuotation.isLoading}
                                    />
                                    <PendingRow
                                        icon="package"
                                        accent="#3182CE"
                                        label={`${ordersMontage.data?.meta?.total} pedido(s) em montagem`}
                                        count={ordersMontage.data?.meta?.total}
                                        onClick={() => navigate("/orders")}
                                        loading={ordersMontage.isLoading}
                                    />
                                    <PendingRow
                                        icon="transit"
                                        accent="#319795"
                                        label={`${ordersWaitingShip.data?.meta?.total} pedido(s) prontos para envio`}
                                        count={ordersWaitingShip.data?.meta?.total}
                                        onClick={() => navigate("/orders")}
                                        loading={ordersWaitingShip.isLoading}
                                    />
                                </Stack>
                            )}
                        </Card>
                    </Scope>

                    {/* Pedidos recentes */}
                    <Scope role="orders_view">
                        <Card
                            title="Pedidos recentes"
                            icon="orders"
                            accent="#008AAC"
                            action={() => navigate("/orders")}
                            actionLabel="Ver todos"
                        >
                            {recentOrders.isLoading ? (
                                <Stack spacing={2}>
                                    {Array.from({ length: 6 }).map((_, i) => (
                                        <Skeleton key={i} height="44px" borderRadius="lg" />
                                    ))}
                                </Stack>
                            ) : !recentOrders.data?.data?.length ? (
                                <Text fontSize="sm" color={muted} textAlign="center" py={6}>
                                    Nenhum pedido encontrado nos últimos 30 dias.
                                </Text>
                            ) : (
                                <Stack spacing={1}>
                                    {recentOrders.data.data.slice(0, 7).map((item) => (
                                        <RecentOrderRow key={item.id} item={item} onOpen={setOrder} />
                                    ))}
                                </Stack>
                            )}
                        </Card>
                    </Scope>

                </Stack>

                {/* ── Right column ────────────────────────────────────── */}
                <Stack spacing={5}>

                    {/* Acesso rápido */}
                    <Card title="Acesso rápido" icon="plug" accent="#008AAC">
                        <SimpleGrid columns={2} gap={3}>
                            <Scope role="orders_view">
                                <QuickButton icon="orders"       label="Pedidos"      accent="#008AAC" onClick={() => navigate("/orders")}       />
                            </Scope>
                            <Scope role="consultants_view">
                                <QuickButton icon="consultants"  label="Consultores"  accent="#38A169" onClick={() => navigate("/consultants")}  />
                            </Scope>
                            <Scope role="distributors_view">
                                <QuickButton icon="distributors" label="Núcleos"      accent="#D69E2E" onClick={() => navigate("/distributors")} />
                            </Scope>
                            <Scope role="withdraws_manage">
                                <QuickButton icon="financial"    label="Saques"       accent="#E53E3E" onClick={() => navigate("/withdraws")}    />
                            </Scope>
                            <Scope role="fiscal_manage">
                                <QuickButton icon="fiscal"       label="Fiscal"       accent="#805AD5" onClick={() => navigate("/fiscal")}       />
                            </Scope>
                            <Scope role="products_view">
                                <QuickButton icon="products"     label="Produtos"     accent="#DD6B20" onClick={() => navigate("/products")}     />
                            </Scope>
                            <Scope role="reports_view">
                                <QuickButton icon="reports"      label="Relatórios"   accent="#319795" onClick={() => navigate("/reports")}      />
                            </Scope>
                            <Scope role="warehouse">
                                <QuickButton icon="warehouse"    label="Expedição"    accent="#718096" onClick={() => navigate("/warehouse")}    />
                            </Scope>
                        </SimpleGrid>
                    </Card>

                    {/* Resumo de status dos pedidos */}
                    <Scope role="orders_view">
                        <Card title="Visão geral de status" icon="status" accent="#008AAC">
                            <Stack spacing={2}>
                                {[
                                    { label: "Análise",       count: ordersAnalyzing.data?.meta?.total,   color: "#DD6B20", loading: ordersAnalyzing.isLoading  },
                                    { label: "Cotação",       count: ordersQuotation.data?.meta?.total,   color: "#D69E2E", loading: ordersQuotation.isLoading  },
                                    { label: "Montagem",      count: ordersMontage.data?.meta?.total,     color: "#3182CE", loading: ordersMontage.isLoading    },
                                    { label: "Pronto p/ envio", count: ordersWaitingShip.data?.meta?.total, color: "#319795", loading: ordersWaitingShip.isLoading },
                                ].map(({ label, count, color, loading }) => (
                                    <HStack
                                        key={label}
                                        justify="space-between"
                                        px={3}
                                        py={2}
                                        borderRadius="lg"
                                    >
                                        <HStack spacing={2}>
                                            <Box w="6px" h="6px" borderRadius="full" bg={color} flexShrink={0} />
                                            <Text fontSize="sm" color={muted}>{label}</Text>
                                        </HStack>
                                        {loading ? (
                                            <Skeleton height="16px" width="24px" borderRadius="sm" />
                                        ) : (
                                            <Text fontSize="sm" fontWeight="bold">
                                                {count ?? 0}
                                            </Text>
                                        )}
                                    </HStack>
                                ))}
                            </Stack>
                        </Card>
                    </Scope>

                </Stack>

            </Grid>

        </Container>
    )
}
